import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Alert, Box, Text, Tooltip } from '@chakra-ui/react'
import {
  HStack,
  Stack,
  SurfaceCard,
  Typography,
  Button,
  DataGrid,
  EmptyState,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Pagination,
  useToast,
} from '@pnld/components-web'

import {
  processings as fetchProcessings,
  processingsMetrics as fetchMetrics,
  createProcessing,
  cancelAllProcessings,
} from '@/api/processing/services'
import DataFilter, { IFilter, TFilter } from '@/components/DataFilter'
import TotalizerDisplay from '@/components/Display/Totalizer'
import {
  Processing,
  DeedType,
  EducationStage,
  LegacyDeedType,
  Status,
} from '@/types/processing'
import { getErrorMessage } from '@/utils/api'
import { stringToBoolean } from '@/utils/string'

import { buildProcessingColumns, getProcessingRowId } from './columns'
import {
  DEED_TYPE_LABEL_WITH_LEGACY,
  EDUCATION_STAGE_LABEL,
  STATUS_LABEL,
} from './constants'
import { ErrorLogContent } from './errorLog'
import NewProcessingModal from './modals/new-processing'
import { NewProcessingFormSubmit } from './modals/new-processing/types'

// Filtro de listagem inclui o legado (Obras EJA) para achar processamentos
// antigos; diferente das opções do form de criação, que não o incluem.
const deedTypeOptions = Object.entries(DEED_TYPE_LABEL_WITH_LEGACY).map(
  ([value, label]) => ({
    label,
    value: value.toString(),
  })
)

const educationStageOptions = Object.entries(EDUCATION_STAGE_LABEL).map(
  ([value, label]) => ({
    label,
    value: value.toString(),
  })
)

const statusOptions = Object.entries(STATUS_LABEL).map(([value, label]) => ({
  label,
  value: value.toString(),
}))

const POLLING_INTERVAL_MS = 3000

const filters: Array<IFilter> = [
  {
    type: 'select',
    name: 'nuDeedType',
    placeholder: 'Tipo de obra',
    options: deedTypeOptions,
  },
  {
    type: 'select',
    name: 'nuEducationStage',
    placeholder: 'Etapa de ensino',
    options: educationStageOptions,
  },
  {
    type: 'select',
    name: 'stStatus',
    placeholder: 'Status',
    options: statusOptions,
  },
]

const ProcessingPage: React.FC = () => {
  const toast = useToast()
  const navigate = useNavigate()

  const [isOpenNewProcessing, setIsOpenNewProcessing] = useState(false)
  const [isOpenErrorModal, setIsOpenErrorModal] = useState(false)
  const [errorLog, setErrorLog] = useState<string | null>(null)
  const [showProcessingQueueAlert, setShowProcessingQueueAlert] =
    useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [isCancellingProcessings, setIsCancellingProcessings] = useState(false)
  const isDev = stringToBoolean(process.env.REACT_APP_DEV_LOGIN)

  const [listData, setListData] = useState<{
    count: number
    results: Processing[]
  } | null>(null)
  const [optimisticItems, setOptimisticItems] = useState<Processing[]>([])
  const [metricsData, setMetricsData] = useState<Record<Status, number> | null>(
    null
  )

  const [pagination, setPagination] = useState<{
    pageIndex: number
    pageSize: number
  }>({
    pageIndex: 0,
    pageSize: 10,
  })

  const [filtersState, setFiltersState] = useState<{
    search?: string
    initialDate?: string
    finalDate?: string
    nuDeedType?: DeedType | LegacyDeedType
    nuEducationStage?: EducationStage
    stStatus?: Status
  }>({})

  const parseDate = useCallback((value?: string | null) => {
    if (!value) return undefined
    const [day, month, year] = value.split('/')
    if (!day || !month || !year) return undefined
    return `${year}-${month}-${day}`
  }, [])

  const handleOnChangeFilter = (filter: TFilter) => {
    const nextFilters = {
      search: filter.search || undefined,
      initialDate: parseDate(filter.startDate as string | null),
      finalDate: parseDate(filter.finalDate as string | null),
      nuDeedType: filter.nuDeedType
        ? (Number(filter.nuDeedType) as DeedType | LegacyDeedType)
        : undefined,
      nuEducationStage: filter.nuEducationStage
        ? (Number(filter.nuEducationStage) as EducationStage)
        : undefined,
      stStatus: filter.stStatus
        ? (Number(filter.stStatus) as Status)
        : undefined,
    }
    setFiltersState(nextFilters)
  }

  const listPayload = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      qnt: pagination.pageSize,
      ...filtersState,
    }),
    [pagination, filtersState]
  )

  const loadList = useCallback(async () => {
    const data = await fetchProcessings(listPayload)
    setListData(data)
    setOptimisticItems(prev =>
      prev.filter(
        opt => !data.results.some(r => r.coProcessing === opt.coProcessing)
      )
    )
  }, [listPayload])

  const loadMetrics = useCallback(async () => {
    const data = await fetchMetrics()
    setMetricsData(data)
  }, [])

  const [refreshFailed, setRefreshFailed] = useState(false)

  const refresh = useCallback(async () => {
    try {
      await Promise.all([loadList(), loadMetrics()])
      setRefreshFailed(false)
    } catch {
      setRefreshFailed(true)
    }
  }, [loadList, loadMetrics])

  useEffect(() => {
    refresh()
  }, [refresh])

  useEffect(() => {
    setPagination(currentPagination =>
      currentPagination.pageIndex === 0
        ? currentPagination
        : { ...currentPagination, pageIndex: 0 }
    )
  }, [filtersState])

  const handleView = useCallback(
    (processing: Processing) =>
      navigate(`/processamento/detalhes/${processing.coProcessing}`),
    [navigate]
  )

  const handleShowErrorLog = useCallback((processing: Processing) => {
    setErrorLog(processing.dsErrorLog)
    setIsOpenErrorModal(true)
  }, [])

  const columns = useMemo(
    () =>
      buildProcessingColumns({
        onView: handleView,
        onShowErrorLog: handleShowErrorLog,
      }),
    [handleView, handleShowErrorLog]
  )

  const serverResults = listData?.results ?? []
  const pendingOptimistic = optimisticItems.filter(
    opt => !serverResults.some(r => r.coProcessing === opt.coProcessing)
  )
  const displayResults = [...pendingOptimistic, ...serverResults]

  const numberOfItems = (listData?.count || 0) + pendingOptimistic.length
  const numberOfPages = Math.max(
    1,
    Math.ceil(numberOfItems / (pagination.pageSize || 1))
  )

  const choicesOfItemsPerPage = useMemo(
    () => [10, 20, 50].map(value => ({ label: String(value), value })),
    []
  )

  const choicesOfPageJump = useMemo(() => {
    const limit = Math.min(numberOfPages, 50)
    return Array.from({ length: limit }, (_, i) => ({
      label: String(i + 1),
      value: i,
    }))
  }, [numberOfPages])

  const handlePrevPage = () => {
    setPagination(p => ({ ...p, pageIndex: Math.max(p.pageIndex - 1, 0) }))
  }
  const handleNextPage = () => {
    setPagination(p => ({
      ...p,
      pageIndex: Math.min(p.pageIndex + 1, Math.max(0, numberOfPages - 1)),
    }))
  }
  const handleJumpToPage = (value: number) => {
    setPagination(p => ({ ...p, pageIndex: value }))
  }
  const handleChangeItemsPerPage = (value: number) => {
    setPagination({ pageIndex: 0, pageSize: value })
  }

  const finished = metricsData?.[Status.CONCLUIDO] || 0
  const inProgress = metricsData?.[Status.PROCESSANDO] || 0
  const pending = metricsData?.[Status.PENDENTE] || 0

  const hasActiveProcessings = pending + inProgress > 0

  // Pelas métricas, e não pela listagem, que é só a página e o filtro atuais.
  const pollingWanted = hasActiveProcessings && !refreshFailed

  const [isPageVisible, setIsPageVisible] = useState(
    () => document.visibilityState === 'visible'
  )

  useEffect(() => {
    const handleVisibilityChange = () => {
      const visible = document.visibilityState === 'visible'
      setIsPageVisible(visible)
      if (visible && pollingWanted) refresh()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [pollingWanted, refresh])

  const shouldPoll = pollingWanted && isPageVisible

  useEffect(() => {
    if (!shouldPoll) return undefined

    let stopped = false
    let timeout: ReturnType<typeof setTimeout>
    const poll = async () => {
      await refresh()
      if (!stopped) timeout = setTimeout(poll, POLLING_INTERVAL_MS)
    }
    timeout = setTimeout(poll, POLLING_INTERVAL_MS)

    return () => {
      stopped = true
      clearTimeout(timeout)
    }
  }, [shouldPoll, refresh])

  const handleCancelAllProcessings = async () => {
    setIsCancellingProcessings(true)
    try {
      const { cancelled } = await cancelAllProcessings()
      toast({
        status: 'success',
        title:
          cancelled === 0
            ? 'Nenhum processamento ativo encontrado.'
            : `${cancelled} processamento(s) cancelado(s).`,
      })
      refresh()
    } catch {
      toast({
        status: 'error',
        title: 'Erro ao cancelar processamentos.',
      })
    } finally {
      setIsCancellingProcessings(false)
    }
  }

  const handleCreateProcessing = async (values: NewProcessingFormSubmit) => {
    try {
      setIsCreating(true)
      const newItem = await createProcessing({
        nuDeedType: values.nuDeedType,
        nuEducationStage: values.nuEducationStage,
        adherenceUploadId: values.adherenceUploadId,
        selectionUploadId: values.selectionUploadId,
        // PSV-424: sem isto o mapa conferido no modal some em silêncio.
        ...(values.serviceMapUploadId
          ? { serviceMapUploadId: values.serviceMapUploadId }
          : {}),
      })

      setOptimisticItems(prev => [newItem, ...prev])
      refresh()

      toast({
        status: 'success',
        title: 'Processamento criado com sucesso!',
      })
      setShowProcessingQueueAlert(true)
      setIsOpenNewProcessing(false)
    } catch (error) {
      toast({
        status: 'error',
        title: getErrorMessage(error),
      })
      throw error
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <Stack spacing={4}>
      {showProcessingQueueAlert && (
        <Alert status="success" borderRadius="md" py="3xs" gap={4}>
          <Stack color="brand.neutral.dark_2">
            <Text fontWeight="bold">
              Estamos a processar os arquivos enviados!
            </Text>
            <Text mt="0 !important">
              O processamento pode demorar alguns minutos. Pode sair da pagina e
              regressar mais tarde para acompanhar o andamento.
            </Text>
          </Stack>
        </Alert>
      )}
      {refreshFailed && (
        <Alert status="warning" borderRadius="md" py="3xs" gap={4}>
          <Text color="brand.neutral.dark_2">
            Não foi possível atualizar a lista de processamentos, e a
            atualização automática foi pausada. Clique em “Atualizar
            Processamentos” para tentar de novo.
          </Text>
        </Alert>
      )}
      <HStack spacing={4} justifyContent="space-between">
        <TotalizerDisplay
          variant="success"
          number={finished}
          label="Processamentos Finalizados"
          icon="book"
          fullWidth
        />
        <TotalizerDisplay
          number={inProgress}
          label="Processamentos Em Andamento"
          icon="book"
          fullWidth
        />
      </HStack>
      <SurfaceCard padding={4}>
        <DataFilter
          id="processing"
          filters={filters}
          onChangeFilter={handleOnChangeFilter}
        />
      </SurfaceCard>
      <SurfaceCard padding={4}>
        <HStack justifyContent="space-between" mb={4}>
          <Typography variant="h-xs" color="brand.primary.dark_2">
            Histórico de Processamentos
          </Typography>
          <HStack spacing={2}>
            {isDev && (
              <Tooltip
                label={
                  hasActiveProcessings
                    ? 'Cancela todos os processamentos pendentes e em andamento.'
                    : 'Nenhum processamento pendente ou em andamento para cancelar.'
                }
                placement="top"
                hasArrow
                maxW="360px"
              >
                <Box display="inline-block">
                  <Button
                    variant="secondary"
                    label="Cancelar Processamentos"
                    iconName="close"
                    border="square"
                    onClick={handleCancelAllProcessings}
                    isLoading={isCancellingProcessings}
                    disabled={isCancellingProcessings || !hasActiveProcessings}
                  />
                </Box>
              </Tooltip>
            )}
            <Button
              variant="secondary"
              label="Atualizar Processamentos"
              iconName="refresh"
              border="square"
              onClick={() => refresh()}
            />
            <Button
              variant="primary"
              label="Novo Processamento"
              iconName="add"
              border="square"
              onClick={() => setIsOpenNewProcessing(true)}
              isLoading={isCreating}
            />
          </HStack>
        </HStack>
        {displayResults.length ? (
          <>
            {/* A paginação vem do servidor. Sem manualPagination o DataGrid
                corta por um render a lista que cresceu e recria as últimas linhas. */}
            <DataGrid
              columns={columns}
              data={displayResults}
              getRowId={getProcessingRowId}
              manualPagination
              hasPagination={false}
            />
            <Pagination
              currentPageIndex={pagination.pageIndex}
              itemsPerPage={pagination.pageSize}
              numberOfItems={numberOfItems}
              numberOfPages={numberOfPages}
              choicesOfItemsPerPage={choicesOfItemsPerPage}
              choicesOfPageJump={choicesOfPageJump}
              onSelectPreviousPage={handlePrevPage}
              onSelectNextPage={handleNextPage}
              onJumpToPage={handleJumpToPage}
              onChangeItemsPerPage={handleChangeItemsPerPage}
            />
          </>
        ) : (
          <EmptyState
            title="Nenhum processamento encontrado"
            description="Ajuste os filtros ou crie um novo processamento."
            shaped="false"
            iconProps={{
              name: 'table',
            }}
          />
        )}
      </SurfaceCard>

      <NewProcessingModal
        isOpen={isOpenNewProcessing}
        onClose={() => setIsOpenNewProcessing(false)}
        onSubmit={handleCreateProcessing}
      />

      <Modal
        isOpen={isOpenErrorModal}
        // Log longo: só o corpo rola, título e Fechar ficam na tela.
        scrollBehavior="inside"
        onClose={() => {
          setIsOpenErrorModal(false)
          setErrorLog(null)
        }}
      >
        <ModalContent maxW="800px">
          <ModalHeader>
            <Typography variant="h-xs" color="brand.neutral.dark_2">
              Detalhes da Falha
            </Typography>
          </ModalHeader>
          <ModalBody>
            <ErrorLogContent errorLog={errorLog} />
          </ModalBody>
          <ModalFooter>
            <Button
              variant="secondary"
              label="Fechar"
              onClick={() => {
                setIsOpenErrorModal(false)
                setErrorLog(null)
              }}
            />
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  )
}

export default ProcessingPage
